import type {
  PaymentQrCreateRequestDto,
  PaymentQrDto,
  PaymentQrListDto,
} from '@app/data/dtos/payments/payment-qr.dto';
import type {
  PaymentQr,
  PaymentQrCreateInput,
  PaymentQrPage,
  PaymentQrStatus,
} from '@app/domain/models/payments-qr/payment-qr.model';
import { PaymentQrError, type PaymentQrErrorKind } from '@app/domain/models/payments-qr/payment-qr.error';
import { parseBackendErrorBody } from '@app/domain/models/errors/backend-error.parser';

const KNOWN_STATUSES: ReadonlySet<string> = new Set(['Pending', 'Reserved', 'Paid', 'Cancelled', 'Expired']);

function toStatus(raw: string | null | undefined): PaymentQrStatus {
  return raw && KNOWN_STATUSES.has(raw) ? (raw as PaymentQrStatus) : 'Unknown';
}

function toAmount(raw: string | number | null | undefined): number {
  if (raw === null || raw === undefined || raw === '') return 0;
  const n = typeof raw === 'string' ? Number(raw) : raw;
  return Number.isFinite(n) ? n : 0;
}

function toIdQr(raw: number | string | null | undefined): number {
  const n = typeof raw === 'string' ? Number(raw) : raw ?? 0;
  return Number.isFinite(n) ? n : 0;
}

function toText(raw: string | null | undefined): string {
  return typeof raw === 'string' ? raw.trim() : '';
}

function toDate(raw: string | null | undefined): string | null {
  return typeof raw === 'string' && raw !== '' ? raw : null;
}

/**
 * El enlace de pago pasa BYTE A BYTE (lleva el `qr_string` firmado en la
 * consulta: re-serializarlo con `URL.href` podría re-codificar la firma). La
 * validación es defensiva y solo decide si se ofrece o no: el enlace únicamente
 * se entrega al portapapeles y a la hoja de compartir del sistema, nunca a
 * `innerHTML` ni a `window.open`, pero un esquema que no sea `https:`
 * (`javascript:`, `data:`, `http:`) no tiene por qué llegar a manos del
 * cliente final con la marca de la suite encima.
 *
 * `new URL()` QUITA espacios en las puntas y tabuladores/saltos de línea
 * internos antes de validar, pero se entrega el texto CRUDO: un enlace con esos
 * caracteres pasaría la validación y llegaría distinto a quien lo pegue. Por eso
 * se rechaza cualquier espacio o carácter de control, en cualquier posición.
 */
const DEEPLINK_FORBIDDEN_CHARS = /[\s\u0000-\u001f\u007f]/;

function toDeeplink(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw === '') return null;
  if (raw !== raw.trim() || DEEPLINK_FORBIDDEN_CHARS.test(raw)) return null;
  try {
    return new URL(raw).protocol === 'https:' ? raw : null;
  } catch {
    return null;
  }
}

/**
 * DTO → entidad. `qr_string` pasa BYTE A BYTE: es una firma y cambiarle un
 * carácter (incluso un espacio) la invalida, así que no se recorta ni normaliza.
 */
export function toPaymentQr(dto: PaymentQrDto): PaymentQr {
  return {
    idQr: toIdQr(dto.id_qr),
    gatewayId: toText(dto.id),
    qrString: typeof dto.qr_string === 'string' ? dto.qr_string : '',
    status: toStatus(dto.status),
    amount: toAmount(dto.amount),
    currency: 'MXN',
    reference: toText(dto.reference),
    description: toText(dto.description),
    idCompany: dto.id_company ?? null,
    companyName: toText(dto.company_name),
    createdByLabel: toText(dto.created_by_label),
    expiresAt: toDate(dto.expires_at),
    paidAt: toDate(dto.paid_at),
    createdAt: toDate(dto.created_at),
    deeplink: toDeeplink(dto.deeplink),
  };
}

/** `count` es el total del servidor (no el largo de la página). */
export function toPaymentQrPage(raw: PaymentQrListDto | null | undefined): PaymentQrPage {
  const results = (raw?.results ?? []).map(toPaymentQr);
  const count = typeof raw?.count === 'number' && Number.isFinite(raw.count) ? raw.count : results.length;
  return { count, results };
}

/** Entrada de dominio → cuerpo del `POST qr/`. Omite lo vacío o indefinido. */
export function toCreateQrRequestDto(input: PaymentQrCreateInput): PaymentQrCreateRequestDto {
  const body: PaymentQrCreateRequestDto = {
    amount: input.amount,
    reference: input.reference.trim(),
    expires_in_minutes: input.expiresInMinutes,
  };
  const description = (input.description ?? '').trim();
  if (description) body.description = description;
  if (input.idCompany !== undefined) body.id_company = input.idCompany;
  return body;
}

const CODE_TO_KIND: Readonly<Record<string, PaymentQrErrorKind>> = {
  PAYMENTS_QR_DISABLED: 'disabled',
  PAYMENTS_QR_REQUIRES_ACCOUNT: 'requires-account',
  PAYMENTS_QR_NOT_CONFIGURED: 'not-configured',
};

function kindByStatus(status: number): PaymentQrErrorKind {
  switch (status) {
    case 0:   return 'network';
    case 403: return 'forbidden';
    case 404: return 'not-found';
    default:  return 'unknown';
  }
}

/**
 * Error HTTP → error de dominio. Clasifica por `error_code` primero y, sin
 * código, por la forma del cuerpo y el status. El texto del backend se lee
 * (dentro del parser) solo para clasificar y NO se copia al error (R8).
 */
export function toPaymentQrError(status: number, body: unknown): PaymentQrError {
  const parsed = parseBackendErrorBody(body, null);
  const code = parsed.code;

  // 401 = credencial rechazada, venga con el código que venga (TOKEN_INVALID, uno
  // desconocido o ninguno). Va ANTES del `error_code`: con código caería en 'coded' y
  // el sondeo lo leería como una falla más (feature 52).
  if (status === 401) return new PaymentQrError('session', status, code);

  if (code) {
    const known = CODE_TO_KIND[code];
    if (known) return new PaymentQrError(known, status, code);
    if (code === 'UPSTREAM_REJECTED' && status === 409) {
      return new PaymentQrError('conflict', status, code);
    }
    return new PaymentQrError('coded', status, code);
  }

  if (status === 400 && parsed.fieldErrors) {
    return new PaymentQrError('validation', status, null, Object.keys(parsed.fieldErrors));
  }

  return new PaymentQrError(kindByStatus(status), status, null);
}
